import React, { useState } from 'react';
import { FaPlus, FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const PreviousClassesCalculator = ({ classes, setClasses, formData, colors, commonStyles }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newRunCount, setNewRunCount] = useState('');

  const addClass = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (newClassName && newRunCount) {
      const runCount = parseInt(newRunCount);
      if (!isNaN(runCount) && runCount > 0) {
        setClasses([...classes, { 
          name: newClassName.trim(), 
          runs: runCount
        }]);
        setNewClassName('');
        setNewRunCount('');
      }
    }
  };

  const removeClass = (index) => {
    setClasses(classes.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addClass();
    }
  };

  const handleRunCountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
      setNewRunCount(value);
    }
  };

  const previousClassesTotal = classes.reduce((total, c) => total + parseInt(c.runs || 0), 0);

  return (
    <div style={{
      marginTop: '0.5rem',
      backgroundColor: colors.background,
      borderRadius: '8px',
      padding: '0.5rem',
      border: `1px solid ${colors.border}`
    }}>
      <style>
        {`
          .previous-classes-grid {
            display: grid;
            grid-template-columns: 1fr 1fr auto;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          
          @media (max-width: 768px) {
            .previous-classes-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
          }
        `}
      </style>

      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: colors.primary,
          fontSize: '1.1rem',
          fontWeight: '600'
        }}
      >
        <span>Calculate Your Overall Draw Position</span>
        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {isExpanded && (
        <div style={{ padding: '1rem 0' }}>
          <div style={{
            backgroundColor: colors.surface,
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: `1px solid ${colors.border}`
          }}>
            <p style={{
              margin: '0 0 1rem 0',
              fontSize: '1rem',
              color: colors.text.secondary
            }}>
              Add any classes that run BEFORE your class to calculate your actual running position.
              This helps you know exactly when you'll run based on all previous classes.
            </p>
            <div style={{
              backgroundColor: colors.background,
              padding: '1rem',
              borderRadius: '4px',
              marginTop: '0.5rem'
            }}>
              <p style={{
                margin: '0',
                fontSize: '1rem',
                color: colors.text.primary
              }}>
                <strong>Example:</strong> If you're draw 166 in the Open class and before your class starts:
                <br/>• Futurity Class: 51 runs
                <br/>• Derby Class: 78 runs
                <br/><strong>Your actual running position would be calculated based on the order of runs (295)</strong>
              </p>
            </div>
          </div>

          <div className="previous-classes-grid" style={{
            marginBottom: '1rem'
          }}>
            <input
              type="text"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Class Name (e.g. Futurity)"
              style={commonStyles.input}
            />
            <input
              type="number"
              value={newRunCount}
              onChange={handleRunCountChange}
              onKeyPress={handleKeyPress}
              placeholder="Total Runs"
              min="1"
              style={commonStyles.input}
            />
            <button
              type="button"
              onClick={addClass}
              style={{
                ...commonStyles.button,
                backgroundColor: colors.primary,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaPlus /> Add
            </button>
          </div>

          {classes.map((c, index) => (
            <div
              key={index}
              style={{
                backgroundColor: colors.surface,
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '0.5rem',
                border: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{ fontWeight: '500', color: colors.text.primary }}>{c.name}</div>
                <div style={{ fontSize: '0.9rem', color: colors.text.secondary }}>
                  {c.runs} total runs
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeClass(index)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.error,
                  cursor: 'pointer'
                }}
              >
                <FaTrash />
              </button>
            </div>
          ))}

          {classes.length > 0 && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: colors.background,
              borderRadius: '4px'
            }}>
              <div style={{
                backgroundColor: colors.surface,
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  color: colors.text.primary,
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}>Draw Position Breakdown</h4>
                {classes.map((c, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem',
                    borderBottom: `1px solid ${colors.border}`
                  }}>
                    <span style={{color: colors.text.primary}}>{c.name} Class:</span>
                    <span style={{fontWeight: '500'}}>{c.runs} runs</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  borderBottom: `1px solid ${colors.border}`,
                  backgroundColor: colors.background
                }}>
                  <span style={{color: colors.text.primary}}>Your Class Draw:</span>
                  <span style={{fontWeight: '500'}}>
                    {formData.isReverseOrder && formData.totalRuns && formData.draw
                      ? `${parseInt(formData.totalRuns) - parseInt(formData.draw)} (Draw #${formData.draw})`
                      : formData.draw || '-'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  marginTop: '0.5rem',
                  backgroundColor: colors.primary + '10',
                  borderRadius: '4px'
                }}>
                  <span style={{fontWeight: 'bold', color: colors.primary}}>Overall Draw Position:</span>
                  <span style={{fontWeight: 'bold', color: colors.primary}}>
                    {formData.draw
                      ? (
                        formData.isReverseOrder && formData.totalRuns
                          ? previousClassesTotal + (parseInt(formData.totalRuns) - parseInt(formData.draw))
                          : previousClassesTotal + parseInt(formData.draw)
                      )
                      : '-'}
                  </span>
                </div>
              </div>
              <div style={{
                marginTop: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text.secondary,
                fontStyle: 'italic'
              }}>
                This represents your actual running position including all previous classes
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreviousClassesCalculator; 